import { useState } from 'react';
import { FiEdit2, FiTrash2, FiHome, FiAlertTriangle, FiClock } from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import clsx from 'clsx';
import RoomHistoryDialog from './RoomHistoryDialog';

const RoomTable = ({ rooms, onEdit, onDelete }) => {
  const [selectedRoom, setSelectedRoom] = useState(null);

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
  };

  const handleEditClick = (e, room) => {
    e.stopPropagation(); // Prevent opening history dialog
    onEdit(room);
  };

  const handleDeleteClick = (e, room) => {
    e.stopPropagation(); // Prevent opening history dialog
    onDelete(room);
  };
  const getStatusDisplay = (room) => {
    // Calcular el estado basado en current_status y has_incidents_today
    if (room.has_incidents_today) {
      return {
        label: 'Incidencia',
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <FiAlertTriangle className="h-3 w-3" />
      };
    }

    switch (room.current_status) {
      case 'maintenance':
        return {
          label: 'Mantenimiento',
          color: 'bg-purple-100 text-purple-800 border-purple-200',
          icon: null
        };
      case 'dirty':
        return {
          label: 'Pendiente',
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: null
        };
      case 'clean':
        return {
          label: 'Limpia',
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: null
        };
      default:
        return {
          label: 'Desconocido',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: null
        };
    }
  };

  if (rooms.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <FiHome className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No se encontraron habitaciones
        </h3>
        <p className="text-gray-500">
          No hay habitaciones que coincidan con los filtros actuales
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Habitación
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Camas
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tiempo Limpieza
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Disposiciones
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rooms.map((room, index) => {
                const statusDisplay = getStatusDisplay(room);

                return (
                  <tr
                    key={room.id}
                    onClick={() => handleRoomClick(room)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors animate-fade-in"
                    style={{ animationDelay: `${index * 0.03}s` }}
                    title="Ver historial de la habitación"
                  >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {room.number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={clsx(
                        'inline-flex items-center space-x-1 text-xs font-semibold px-3 py-1 rounded-full border',
                        statusDisplay.color
                      )}
                    >
                      {statusDisplay.icon}
                      <span>{statusDisplay.label}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <IoBedOutline className="h-5 w-5 text-gray-600 mr-2" />
                      <span className="font-semibold">{room.bed_count}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {room.cleaning_time_minutes ? (
                      <div className="flex items-center text-sm text-gray-900">
                        <FiClock className="h-4 w-4 text-gray-500 mr-2" />
                        <span>{room.cleaning_time_minutes} min</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {room.bed_dispositions && room.bed_dispositions.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {room.bed_dispositions.map((disposition) => (
                          <span
                            key={disposition.id}
                            className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap"
                          >
                            {disposition.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {room.notes || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => handleEditClick(e, room)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50"
                        title="Editar habitación"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteClick(e, room)}
                        className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                        title="Eliminar habitación"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Room History Dialog */}
      {selectedRoom && (
        <RoomHistoryDialog
          room={selectedRoom}
          onClose={() => setSelectedRoom(null)}
        />
      )}
    </div>
  );
};

export default RoomTable;